import { useCallback, useEffect, useState } from "react";
import { History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

// Past publishes, each one can be restored (Super Admins only).
export const HistoryPanel = ({ onRestored }) => {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => { api.get("/design/history").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([])); }, []);
  useEffect(() => { load(); }, [load]);
  const restore = async (h, which) => {
    const msg = which === "before" ? "Put the website back the way it was just BEFORE this publish?" : "Put the website back to this version?";
    if (!window.confirm(`${msg} This replaces the current colors, fonts, sections, photos, removed items and website text. You can undo it from this list too.`)) return;
    setBusy(`${h.id}-${which}`);
    try {
      await api.post(`/design/history/${h.id}/restore?which=${which}`);
      toast.success("Website restored. It's live now.");
      load();
      onRestored();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  if (!list) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-[#C8862F]" />;
  return (
    <div className="grid gap-2" data-testid="ve-history-list">
      <p className="text-xs text-[#4A5568]">Every time you press PUBLISH (or restore), a copy of the website's look is saved here.</p>
      {!list.length && <p data-testid="ve-history-empty" className="py-4 text-center text-xs text-[#5F6875]">No publishes yet.</p>}
      {list.map((h, i) => (
        <div key={h.id} data-testid={`ve-history-${i}`} className="rounded-xl border border-[#14213D]/10 bg-white p-3">
          <p className="flex items-center gap-1.5 font-ui text-xs font-semibold text-[#14213D]"><History className="h-3.5 w-3.5 text-[#8A6A12]" /> {new Date(h.created_at).toLocaleString()}{i === 0 && <span className="rounded-full bg-[#2F855A]/10 px-2 py-0.5 text-[10px] text-[#2F855A]">Live now</span>}</p>
          <p className="mt-0.5 text-[11px] text-[#4A5568]">{h.kind === "restore" ? `Restored: ${h.label}` : `Published ${h.changes} change(s)`} · by {h.by}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {i > 0 && <button type="button" data-testid={`ve-history-restore-${i}`} disabled={!!busy} onClick={() => restore(h, "after")} className="rounded-full bg-[#14213D] px-3 py-1 font-ui text-[11px] font-semibold text-white disabled:opacity-50">{busy === `${h.id}-after` ? "Restoring…" : "Restore this version"}</button>}
            <button type="button" data-testid={`ve-history-restore-before-${i}`} disabled={!!busy} onClick={() => restore(h, "before")} className="rounded-full border border-[#14213D]/20 px-3 py-1 font-ui text-[11px] font-semibold text-[#14213D] disabled:opacity-50">{busy === `${h.id}-before` ? "Restoring…" : "Undo: go to the version before this"}</button>
          </div>
        </div>
      ))}
    </div>
  );
};
